/**
 * State a list remembers across visits, in this browser.
 *
 * Read once as the initial state, so a remembered value paints on the first
 * render rather than replacing a default a frame later. A blocked or
 * unavailable store, or a value that no longer reads, falls back to the
 * initial value: a preference is never a reason to fail the screen.
 *
 * @module components/ui/useStoredState
 */

import { type Dispatch, type SetStateAction, useEffect, useState } from "react";

/**
 * @param key - The `localStorage` key; absent, the state is not remembered.
 * @param initial - Used when nothing is stored, or the stored value is refused.
 * @param read - Narrows the parsed value, or returns `undefined` to refuse it.
 */
export function useStoredState<T>(
  key: string | undefined,
  initial: T,
  read: (stored: unknown) => T | undefined,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    if (key === undefined) {
      return initial;
    }
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? initial : (read(JSON.parse(raw)) ?? initial);
    } catch {
      return initial;
    }
  });

  // Written after commit, so a value set during render is stored once it lands.
  useEffect(() => {
    if (key === undefined) {
      return;
    }
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Not remembered; the screen still works.
    }
  }, [key, value]);

  return [value, setValue];
}
